import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FaInstagram, FaFacebook, FaEtsy, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// ---------- Images ----------
import deerImage from '../images/deer.jpeg';
import dollImage from '../images/doll.jpeg';
import duckImage from '../images/duck.png';
import ducksImage from '../images/ducks.jpeg';
import duckyImage from '../images/ducky.jpeg';
import mallardImage from '../images/mallard.jpeg';
import mouseImage from '../images/mouse.jpeg';
import rugImage from '../images/rug.jpeg';

// ---------- Styled Components ----------
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 6rem auto 4rem;
  padding: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  color: #7A4988;
  font-size: 2.4rem;
  margin-bottom: 0.75rem;
  font-family: 'Poppins', sans-serif;
`;

const Subtitle = styled.p`
  color: #444;
  max-width: 720px;
  margin: 0 auto 2rem;
  font-size: 1.05rem;
  line-height: 1.7;
  font-family: 'Poppins', sans-serif;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.2rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.button`
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
`;

const CardInner = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(122, 73, 136, 0.12);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  ${Card}:hover & {
    transform: translateY(-4px);
    box-shadow: 0 10px 24px rgba(122, 73, 136, 0.18);
  }
`;

const Thumb = styled.img`
  width: 100%;
  height: 260px;
  object-fit: cover;
  display: block;

  @media (max-width: 520px) {
    height: 240px;
  }
`;

const CardMeta = styled.div`
  padding: 0.9rem 1rem 1.1rem;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: #7A4988;
  font-family: 'Dancing Script', cursive;
  font-size: 1.6rem;
`;

const CardHint = styled.p`
  margin: 0.25rem 0 0;
  color: #666;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
`;

// ---------- Lightbox ----------
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 10, 25, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 9999;
`;

const Lightbox = styled.div`
  width: min(980px, 100%);
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.35);
`;

const LightboxTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  background: linear-gradient(90deg, rgba(122, 73, 136, 0.12), rgba(255, 170, 215, 0.18));
`;

const LightboxTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const LightboxName = styled.h4`
  margin: 0;
  color: #7A4988;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
`;

const LightboxCount = styled.span`
  color: #666;
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
`;

const IconBtn = styled.button`
  border: none;
  background: #FF85C0;
  color: #fff;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 3px 10px rgba(255, 170, 215, 0.45);

  &:hover {
    background: #7A4988;
    transform: translateY(-2px);
  }
`;

const LightboxBody = styled.div`
  position: relative;
  background: #111;
`;

const FullImage = styled.img`
  width: 100%;
  height: min(70vh, 650px);
  object-fit: contain;
  display: block;
`;

const NavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: rgba(255, 133, 192, 0.9);
  color: #fff;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(122, 73, 136, 0.92);
  }
`;

const NavLeft = styled(NavBtn)`left: 12px;`;
const NavRight = styled(NavBtn)`right: 12px;`;

// ---------- Footer ----------
const Footer = styled.footer`
  padding: 3rem 2rem 1.5rem;
  background-color: #7A4988;
  color: #fff;
  font-family: 'Poppins', sans-serif;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1.2rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    width: 30px;
    height: 2px;
    background-color: #FFAAD7;
  }
`;

const FooterLink = styled.a`
  color: #F3E3F7;
  text-decoration: none;
  margin-bottom: 0.8rem;
  transition: all 0.3s ease;

  &:hover {
    color: #FFAAD7;
    transform: translateX(3px);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const SocialIcon = styled.a`
  color: #F3E3F7;
  font-size: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    color: #FFAAD7;
    transform: translateY(-3px);
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.9rem;
`;

// ---------- Component ----------
function Gallery() {
  const items = useMemo(
    () => [
      { src: deerImage, title: 'Squirrel Crochet Plushie', hint: 'Tap to view full size' },
      { src: dollImage, title: 'Fairy Ballerina Plushie', hint: 'Tap to view full size' },
      { src: duckImage, title: 'Mallard Duck Lovey', hint: 'Tap to view full size' },
      { src: ducksImage, title: 'Mallard Duck Loveys', hint: 'Tap to view full size' },
      { src: duckyImage, title: 'Jumbo Mallard Duck Plushie', hint: 'Tap to view full size' },
      { src: mallardImage, title: 'Duck Plushie', hint: 'Tap to view full size' },
      { src: mouseImage, title: 'If You Give A Mouse A Cookie Book Buddy Set', hint: 'Tap to view full size' },
      { src: rugImage, title: 'Retro Daisy Afghan Blanket', hint: 'Tap to view full size' },
    ],
    []
  );

  const [activeIndex, setActiveIndex] = useState(null);

  const isOpen = activeIndex !== null;
  const activeItem = isOpen ? items[activeIndex] : null;

  const close = () => setActiveIndex(null);

  const prev = (e) => {
    e?.stopPropagation();
    setActiveIndex((i) => (i === 0 ? items.length - 1 : i - 1));
  };

  const next = (e) => {
    e?.stopPropagation();
    setActiveIndex((i) => (i === items.length - 1 ? 0 : i + 1));
  };

  const onKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
  };

  return (
    <>
      <PageContainer tabIndex={-1} onKeyDown={onKeyDown}>
        <Title>Gallery</Title>
        <Subtitle>
          Peek at some of our favorite handmade pieces — click any photo to view it larger.
        </Subtitle>

        <GalleryGrid>
          {items.map((item, idx) => (
            <Card key={item.title + idx} onClick={() => setActiveIndex(idx)} aria-label={`Open ${item.title}`}>
              <CardInner>
                <Thumb src={item.src} alt={item.title} />
                <CardMeta>
                  <CardTitle>{item.title}</CardTitle>
                  <CardHint>{item.hint}</CardHint>
                </CardMeta>
              </CardInner>
            </Card>
          ))}
        </GalleryGrid>
      </PageContainer>

      {isOpen && (
        <Overlay onClick={close}>
          <Lightbox onClick={(e) => e.stopPropagation()}>
            <LightboxTop>
              <LightboxTitle>
                <LightboxName>{activeItem.title}</LightboxName>
                <LightboxCount>
                  {activeIndex + 1} / {items.length}
                </LightboxCount>
              </LightboxTitle>

              <IconBtn onClick={close} aria-label="Close">
                <FaTimes />
              </IconBtn>
            </LightboxTop>

            <LightboxBody>
              <NavLeft onClick={prev} aria-label="Previous">
                <FaChevronLeft />
              </NavLeft>

              <FullImage src={activeItem.src} alt={activeItem.title} />

              <NavRight onClick={next} aria-label="Next">
                <FaChevronRight />
              </NavRight>
            </LightboxBody>
          </Lightbox>
        </Overlay>
      )}

      <Footer>
        <FooterContent>
          <FooterSection>
            <FooterTitle>Quick Links</FooterTitle>
            <FooterLink href="/">Home</FooterLink>
            <FooterLink href="/ready-to-ship">Ready to Ship</FooterLink>
            <FooterLink href="/custom-order">Custom Orders</FooterLink>
            <FooterLink href="/faq">FAQ</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Policies</FooterTitle>
            <FooterLink href="/shipping">Shipping</FooterLink>
            <FooterLink href="/returns">Returns</FooterLink>
            <FooterLink href="/privacy">Privacy Policy</FooterLink>
            <FooterLink href="/terms">Terms of Service</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Connect With Us</FooterTitle>
            <p>Follow us for updates and behind-the-scenes content!</p>
            <SocialLinks>
              <SocialIcon href="https://www.instagram.com/lavenderandpalmcrochet/">
                <FaInstagram />
              </SocialIcon>
              <SocialIcon href="https://www.facebook.com/people/Lavender-and-Palm-Crochet/61566023416208/#">
                <FaFacebook />
              </SocialIcon>
              <SocialIcon href="https://lavenderandpalm.etsy.com">
                <FaEtsy />
              </SocialIcon>
            </SocialLinks>
          </FooterSection>
        </FooterContent>

        <Copyright>
          © {new Date().getFullYear()} Lavender & Palm Co. All rights reserved.
        </Copyright>
      </Footer>
    </>
  );
}

export default Gallery;